<template>
  <q-modal
    ref="modal"
    v-model="modalOpened"
    name="create-project-modal"
    content-classes="default-modal width-sm"
    :title="title"
    :autofocus="$refs.projectName"
  >
    <q-stepper ref="stepper" v-model="step" class="no-shadow">
      <div class="modal-body big-modal-scroll col scroll" style="min-height: 280px">
        <q-step default title="Projektdaten" name="base">
          <div class="q-px-sm q-py-lg">
            <div class="row items-center gutter-x-md">
              <div class="col">
                <q-field class="q-ma-none" :error="errors.has('name')" :error-label="errors.first('name')">
                  <q-input
                    ref="projectName"
                    name="name"
                    v-model="formData.name"
                    v-validate.disable="'required|max:255'"
                    :placeholder="$t('name')"
                    :float-label="$t('name')"
                    @keydown.enter="handleSaveProject"
                  />
                </q-field>
              </div>

              <div class="col-auto">
                <div
                  class="project__color-picker circle"
                  :style="{ 'background-color': formData.colorHex }"
                ></div>

                <color-picker-popover
                  ref="colorPicker"
                  v-model="formData.colorHex"
                  :colors="projectColors"
                />
              </div>
            </div>

            <div class="row">
              <div class="col-12">
                <customer-input
                  v-model="formData.customer"
                  :float-label="$tc('customer', 1)"
                  :placeholder="null"
                />
              </div>
            </div>

            <div class="row gutter-x-md">
              <div class="col-6">
                <q-field :error="errors.has('startDate')" :error-label="errors.first('startDate')">
                  <q-datetime
                    type="date"
                    name="startDate"
                    v-model="formData.startDate"
                    :float-label="$t('start_date')"
                    :placeholder="$t('start_date')"
                  />
                </q-field>
              </div>

              <div class="col-6">
                <q-field :error="errors.has('endDate')" :error-label="errors.first('endDate')">
                  <q-datetime
                    type="date"
                    name="endDate"
                    v-model="formData.endDate"
                    :float-label="$t('end_date')"
                    :placeholder="$t('end_date')"
                  />
                </q-field>
              </div>
            </div>
          </div>
        </q-step>

        <q-step :title="$t('project_budget')" name="budget">
          <div class="q-px-sm q-py-lg">
            <div class="row">
              <div class="col-12 flex justify-center">
                <q-btn-toggle
                  v-model="formData.projectType"
                  :options="projectTypesSelectOptions"
                  toggle-color="light"
                  class="no-shadow"
                  style="border: 1px solid var(--q-color-light)"
                />
              </div>
            </div>

            <div class="row gutter-x-md">
              <div v-if="showHourlyRateField" class="col-6">
                <q-field :error="errors.has('rate')" :error-label="errors.first('rate')">
                  <q-input
                    type="number"
                    name="rate"
                    v-model="formData.rate"
                    v-validate.disable="'decimal:2|min:0'"
                    :placeholder="$t('hourly_rate')"
                    :float-label="$t('hourly_rate')"
                    :suffix="defaultCurrencySymbol + '/' + $tc('hour')"
                  />
                </q-field>
              </div>

              <div :class="{ 'col-6': showHourlyRateField, 'col-12': !showHourlyRateField }">
                <q-field :error="errors.has('estimatedHours')" :error-label="errors.first('estimatedHours')">
                  <q-input
                    type="number"
                    name="estimatedHours"
                    v-model="formData.estimatedHours"
                    v-validate.disable="'numeric|min:0'"
                    :placeholder="$t('estimated_hours')"
                    :float-label="$t('estimated_hours')"
                    :suffix="$tc('hour', formData.estimatedHours)"
                    clearable
                  />
                </q-field>
              </div>
            </div>

            <q-field v-if="showBudgetField" :error="errors.has('budget')" :error-label="errors.first('budget')">
              <q-input
                type="number"
                name="budget"
                v-model="formData.budget"
                v-validate.disable="'decimal:2|min:0'"
                :placeholder="$t('project_budget')"
                :float-label="$t('project_budget')"
                :suffix="defaultCurrencySymbol"
              />
            </q-field>
          </div>
        </q-step>
      </div>

      <div class="modal-footer">
        <div class="row justify-between full-width">

          <div
            v-if="step !== 'budget'"
            class="order-first"
          >
            <!-- Dummy to push btn -->
          </div>

          <q-btn
            v-if="step !== 'budget'"
            :label="$t('next')"
            class="order-last"
            flat
            @click="handleStepNext"
          />

          <q-btn
            v-if="step !== 'base'"
            :label="$t('back')"
            class="order-last"
            @click="handleStepPrevious"
            flat
          />

          <q-btn
            v-if="step !== 'base'"
            ref="submitBtn"
            color="positive"
            class="order-last"
            :disabled="!this.formData.name"
            @click="handleSaveProject"
          >
            {{ $t('save') }}
          </q-btn>
        </div>
      </div>
    </q-stepper>
  </q-modal>
</template>

<script>
  import { general, projects } from 'src/config'
  import { projectTypes, projectTypesSelectOptions } from 'src/utils/projectTypes'
  import ColorPickerPopover from '../ColorPickerPopover.vue'
  import CustomerInput from 'components/Common/Inputs/CustomerInput.vue'
  import { CustomersClient } from 'src/api/Customers'
  import Decimal from 'decimal.js'
  import DefaultModal from 'components/Modal/DefaultModal.vue'
  import { date } from 'quasar'
  import debounceMixin from 'src/utils/mixins/debounce'
  import { getCurrencySymbol } from 'src/utils/money'
  import { getCustomerName } from 'src/utils/searchResultParser'
  import { mapGetters } from 'vuex'
  import parseDate from 'date-fns/parse'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    props: {
      project: {
        type: Object
      },
      customer: {
        type: Object
      }
    },

    data () {
      return {
        step: null,
        formData: {
          name: null,
          colorHex: null,
          customer: null,
          startDate: null,
          endDate: null,
          projectType: projectTypes.TIME_AND_MATERIALS,
          estimatedHours: null,
          rate: null,
          budget: null
        },
        modalOpened: false
      }
    },

    computed: {
      ...mapGetters(['activeTeam']),

      projectTypesSelectOptions: () => projectTypesSelectOptions,

      projectColors () {
        return projects.colors
      },

      hasCustomer () {
        return !!this.project.customer_id && this.project.customer
      },

      defaultCurrencySymbol () {
        return getCurrencySymbol(general.base_currency)
      },

      title () {
        return this.updating ? this.$t('edit_project') : this.$t('create_project')
      },

      updating () {
        return !!this.project
      },

      showHourlyRateField () {
        return ![projectTypes.NON_BILLABLE, projectTypes.FIXED_FEE].includes(this.formData.projectType)
      },

      showBudgetField () {
        return this.formData.projectType !== projectTypes.NON_BILLABLE
      },

      rate () {
        return this.formData.rate
      },

      estimatedHours () {
        return this.formData.estimatedHours
      }
    },

    methods: {
      handleSearch (query, done) {
        new CustomersClient()
          .search(query)
          .then(response => {
            return done(this.mapSearchResponse(response.data.data))
          })
      },

      mapSearchResponse (data) {
        return data.map(entry => {
          return {
            label: getCustomerName(entry),
            value: entry,
            icon: entry.type === 'person' ? 'person' : 'business'
          }
        })
      },

      handleSaveProject () {
        // Set a 200ms timeout because of https://github.com/quasarframework/quasar/issues/2113
        setTimeout(() => {
          this.$validator.validateAll()
            .then(result => {
              if (!result) {
                return
              }

              const data = Object.assign({}, this.formData, {
                rate: this.priceInCents(this.formData.rate),
                customerId: this.formData.customer ? this.formData.customer.id : null,
                startDate: this.formData.startDate ? date.formatDate(this.formData.startDate, 'YYYY-MM-DD') : null,
                endDate: this.formData.endDate ? date.formatDate(this.formData.endDate, 'YYYY-MM-DD') : null,
                budget: this.priceInCents(this.formData.budget)
              })

              this.$emit('save', data)

              this.resetData()

              this.modalOpened = false
            })
        }, 200)
      },

      show () {
        this.modalOpened = true

        this.$refs.stepper.reset()
        this.$validator.reset()

        if (this.updating) {
          this.formData = {
            name: this.project.name,
            colorHex: this.project.color_hex,
            customer: this.project.customer,
            startDate: this.project.start_date ? parseDate(this.project.start_date.date) : null,
            endDate: this.project.end_date ? parseDate(this.project.end_date.date) : null,
            projectType: this.project.project_type,
            estimatedHours: this.project.estimated_hours,
            rate: this.project.rate ? new Decimal(this.project.rate).div(100) : null,
            budget: this.project.budget ? new Decimal(this.project.budget).div(100) : null
          }
        } else {
          this.formData.colorHex = this.projectColors[Math.floor(Math.random() * this.projectColors.length)]
          this.formData.customer = this.customer || null
        }
      },

      resetData () {
        this.formData = {
          name: null,
          colorHex: null,
          customer: null,
          estimatedHours: null,
          rate: null
        }
      },

      priceInCents (value) {
        if (typeof value !== 'number') {
          return
        }

        return new Decimal(value).times(100).round().toNumber()
      },

      handleStepPrevious () {
        // Set a 200ms timeout because of https://github.com/quasarframework/quasar/issues/2113
        setTimeout(() => {
          this.$validator.validateAll()
            .then(result => {
              if (!result) {
                return
              }

              this.$refs.stepper.previous()
            })
        }, 200)
      },

      handleStepNext () {
        // Set a 200ms timeout because of https://github.com/quasarframework/quasar/issues/2113
        setTimeout(() => {
          this.$validator.validateAll()
            .then(result => {
              if (!result) {
                return
              }

              this.$refs.stepper.next()
            })
        }, 200)
      }
    },

    watch: {
      showBudgetField (value) {
        if (!value) {
          this.formData.budget = null
        }
      },

      rate (rate) {
        if (rate && this.estimatedHours) {
          this.formData.budget = rate * this.estimatedHours
        }
      },

      estimatedHours (estimatedHours) {
        if (estimatedHours && this.rate) {
          this.formData.budget = estimatedHours * this.rate
        }
      }
    },

    components: {
      CustomerInput,
      ColorPickerPopover,
      DefaultModal
    },

    mixins: [
      debounceMixin
    ]
  }
</script>

<style lang="stylus">
  .project__color-picker
    height: 24px
    width: 24px
    border: 1px solid $grey-2
    cursor: pointer
</style>
